import { useEffect, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, ExternalLink, RefreshCw, Sparkles } from 'lucide-react'
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useOpsReviewList, useOpsReviewDetail, useOpsReviewRange, useTriggerOpsReview } from '@/hooks/useOpsReviews'
import type { OpsReview, MetricBag, Trend, FunnelDay, OpsReviewRangeRow } from '@/lib/api/ops-reviews'

// ── helpers ─────────────────────────────────────────────────────────────────

const num = (m: MetricBag, src: string, key: string): number => {
  const v = m?.[src]?.[key]
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const has = (m: MetricBag, src: string) => !!m?.[src]

function trendOf(trends: Record<string, Record<string, unknown>>, src: string, key: string): Trend | null {
  const t = trends?.[src]?.[key] as Trend | undefined
  return t && typeof t === 'object' ? t : null
}

function TrendArrow({ t }: { t: Trend | null }) {
  if (!t || t.pct == null) return <span className="text-xs text-muted-foreground">—</span>
  const up = t.pct > 0
  return (
    <span className={`text-xs font-semibold ${up ? 'text-green-600' : 'text-red-600'}`}>
      {up ? '▲' : '▼'} {Math.abs(t.pct)}%
    </span>
  )
}

function Kpi({ label, value, trend, badge }: { label: string; value: string; trend?: Trend | null; badge?: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label} {badge}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend !== undefined && <TrendArrow t={trend ?? null} />}
      </CardContent>
    </Card>
  )
}

// Legacy overlaps Pilot/Pro — it is the grandfathered cohort, not a tier.
const TIER_ROW: [label: string, key: string][] = [
  ['Trial', 'trial_users'],
  ['Pilot', 'pilot_users'],
  ['Pro', 'pro_users'],
  ['Business', 'business_users'],
  ['Legacy caps', 'legacy_caps_users'],
]

const sevColor: Record<string, string> = {
  crit: 'bg-red-600 text-white',
  warn: 'bg-amber-600 text-white',
  info: 'bg-blue-700 text-white',
}

// ── page ──────────────────────────────────────────────────────────────────────

export function OpsReviewPage() {
  const list = useOpsReviewList()
  const trigger = useTriggerOpsReview()
  const [mode, setMode] = useState<'day' | 'range'>('day')
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const dates = useMemo(() => (list.data ?? []).map((r) => r.review_date), [list.data])

  // Default selection once the list lands.
  useEffect(() => {
    if (!dates.length) return
    if (!selectedDate) setSelectedDate(dates[0])
    if (!to) setTo(dates[0])
    if (!from) setFrom(dates[Math.min(dates.length - 1, 13)] ?? dates[dates.length - 1])
  }, [dates, selectedDate, from, to])

  const detail = useOpsReviewDetail(selectedDate)
  const range = useOpsReviewRange(from, to, mode === 'range')

  return (
    <div>
      <PageHeader
        title="Ops Reviews"
        description={
          list.data?.[0]
            ? `Latest ${list.data[0].review_date} · generated ${formatDistanceToNow(new Date(list.data[0].generated_at), { addSuffix: true })}`
            : 'cc-ops-review daily digest'
        }
        actions={
          <Button
            size="sm"
            variant="outline"
            disabled={trigger.isPending}
            onClick={() => trigger.mutate({ window: '1d', notify: false })}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${trigger.isPending ? 'animate-spin' : ''}`} />
            {trigger.isPending ? 'Queued…' : 'Run now'}
          </Button>
        }
      />

      {trigger.isSuccess && (
        <p className="mb-4 text-xs text-muted-foreground">
          Collection queued — the new row appears here in ~30–90s. This view auto-refreshes.
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={mode} onValueChange={(v) => setMode(v as 'day' | 'range')}>
          <TabsList>
            <TabsTrigger value="day">Single day</TabsTrigger>
            <TabsTrigger value="range">Date range</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === 'day' ? (
          <select
            data-testid="ops-day-select"
            className="h-9 rounded-md border bg-background px-3 text-sm"
            value={selectedDate ?? ''}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {dates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <input type="date" className="h-9 rounded-md border bg-background px-3" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-muted-foreground">→</span>
            <input type="date" className="h-9 rounded-md border bg-background px-3" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        )}
      </div>

      {mode === 'day' ? (
        list.isLoading || detail.isLoading ? (
          <LoadingGrid />
        ) : detail.error || !detail.data ? (
          <EmptyState message="No review for this day." />
        ) : (
          <DayView review={detail.data} />
        )
      ) : range.isLoading ? (
        <LoadingGrid />
      ) : !range.data?.length ? (
        <EmptyState message="No reviews in this range." />
      ) : (
        <RangeView rows={range.data} />
      )}
    </div>
  )
}

function LoadingGrid() {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="p-6 text-center text-muted-foreground">
        <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
        <p>{message}</p>
      </CardContent>
    </Card>
  )
}

// ── single-day view ─────────────────────────────────────────────────────────

function DayView({ review }: { review: OpsReview }) {
  const m = review.metrics
  const t = review.trends
  const live = m?.stripe?.mode === 'live'
  const funnel = (t?.posthog?.funnel_by_day as FunnelDay[] | undefined) ?? []

  return (
    <div className="space-y-6" data-testid="ops-day-view">
      {/* health + provenance */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            Health summary
            <Badge variant="secondary" className="text-[10px]">
              {review.sources_healthy}/{review.sources_total} sources
            </Badge>
            <Badge className={`text-[10px] ${review.source === 'claude' ? 'bg-blue-700 text-white' : ''}`} variant={review.source === 'claude' ? 'default' : 'outline'}>
              {review.source === 'claude' ? <><Sparkles className="mr-1 h-3 w-3" />LLM insights</> : 'metrics-only'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {Object.entries(review.section_health).map(([src, ok]) => (
              <Badge key={src} variant={ok ? 'secondary' : 'destructive'} className="text-[10px]">{src}</Badge>
            ))}
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {review.health_summary.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          {review.html_url && (
            <Button asChild size="sm" variant="outline" className="mt-4">
              <a href={review.html_url} target="_blank" rel="noreferrer" data-testid="ops-open-report">
                <ExternalLink className="mr-2 h-4 w-4" /> Open full report
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {has(m, 'supabase') && <Kpi label="New users" value={num(m, 'supabase', 'new_users').toLocaleString()} trend={trendOf(t, 'supabase', 'new_users')} />}
        {has(m, 'supabase') && <Kpi label="New layouts" value={num(m, 'supabase', 'new_layouts').toLocaleString()} trend={trendOf(t, 'supabase', 'new_layouts')} />}
        {has(m, 'posthog') && <Kpi label="DAU" value={num(m, 'posthog', 'dau').toLocaleString()} trend={trendOf(t, 'posthog', 'dau')} />}
        {has(m, 'posthog') && <Kpi label="Events" value={num(m, 'posthog', 'total_events').toLocaleString()} />}
        {has(m, 'posthog') && <Kpi label="Signups" value={num(m, 'posthog', 'signups').toLocaleString()} />}
        {has(m, 'posthog') && <Kpi label="Paid" value={num(m, 'posthog', 'paid').toLocaleString()} />}
        {has(m, 'stripe') && (
          <Kpi
            label="Gross"
            value={'$' + num(m, 'stripe', 'gross_amount').toLocaleString()}
            badge={<Badge className={`text-[9px] ${live ? 'bg-red-600 text-white' : ''}`} variant={live ? 'default' : 'outline'}>{live ? 'LIVE' : 'test'}</Badge>}
          />
        )}
        {has(m, 'sentry') && <Kpi label="New issues" value={num(m, 'sentry', 'genuine_new_issues').toLocaleString()} />}
        {has(m, 'cloudflare') && <Kpi label="Requests" value={num(m, 'cloudflare', 'requests').toLocaleString()} />}
        {has(m, 'cloudflare') && <Kpi label="5xx rate" value={(num(m, 'cloudflare', 'error_rate_5xx') * 100).toFixed(2) + '%'} />}
      </div>

      {/* subscribers by tier — reviews collected before the Business tier carry no business_users */}
      {has(m, 'supabase') && m.supabase.business_users !== undefined && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Subscribers by tier</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="ops-tier-row">
            {TIER_ROW.map(([label, key]) => (
              <span key={key}>
                <span className="text-muted-foreground">{label}</span>{' '}
                <span className="font-semibold">{num(m, 'supabase', key).toLocaleString()}</span>
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      {/* funnel-by-day */}
      {funnel.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Funnel by day</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={funnel} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="dau" stroke="#16428B" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="app_opened" stroke="#64A735" dot={false} />
                <Line type="monotone" dataKey="engaged" stroke="#C77700" dot={false} />
                <Line type="monotone" dataKey="signups" stroke="#9333ea" dot={false} />
                <Line type="monotone" dataKey="paid" stroke="#B21E23" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* insights (when present) */}
      {review.insights?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Insights</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {review.insights.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* anomalies */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Anomalies</CardTitle></CardHeader>
        <CardContent>
          {review.anomalies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No anomalies flagged this window.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="py-1.5 pr-3">Sev</th><th className="pr-3">Source</th><th className="pr-3">Message</th><th>Value</th>
                </tr>
              </thead>
              <tbody>
                {review.anomalies.map((a, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1.5 pr-3"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sevColor[a.severity] ?? ''}`}>{a.severity}</span></td>
                    <td className="pr-3">{a.source}</td>
                    <td className="pr-3">{a.message}</td>
                    <td className="text-muted-foreground">{a.value ?? ''}{a.baseline != null ? ` / ${a.baseline}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* action queue */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Action queue ({review.actionables.length})</CardTitle></CardHeader>
        <CardContent>
          {review.actionables.length === 0 ? (
            <p className="text-sm text-muted-foreground">Action queue is empty.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="py-1.5 pr-3">Source</th><th className="pr-3">Kind</th><th className="pr-3">Item</th><th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {review.actionables.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="py-1.5 pr-3">{a.source}</td>
                    <td className="pr-3"><Badge variant="outline" className="text-[10px]">{a.kind}</Badge></td>
                    <td className="pr-3">{a.url ? <a className="text-primary underline" href={a.url} target="_blank" rel="noreferrer">{a.title}</a> : a.title}</td>
                    <td className="text-muted-foreground">{a.recommended_next_step}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── range view ──────────────────────────────────────────────────────────────

function RangeView({ rows }: { rows: OpsReviewRangeRow[] }) {
  const series = rows.map((r) => ({
    date: r.review_date,
    new_users: num(r.metrics, 'supabase', 'new_users'),
    dau: num(r.metrics, 'posthog', 'dau'),
    requests: num(r.metrics, 'cloudflare', 'requests'),
    gross_amount: num(r.metrics, 'stripe', 'gross_amount'),
    genuine_new_issues: num(r.metrics, 'sentry', 'genuine_new_issues'),
  }))

  return (
    <div className="space-y-6" data-testid="ops-range-view">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Acquisition &amp; engagement</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
              <Line type="monotone" dataKey="dau" stroke="#16428B" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="new_users" stroke="#64A735" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Edge requests &amp; new issues</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="requests" stroke="#16428B" dot={false} />
                <Line type="monotone" dataKey="genuine_new_issues" stroke="#B21E23" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Gross revenue / day</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
                <Bar dataKey="gross_amount" fill="#64A735" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
